import React, { useState, useEffect, useRef } from "react"
import { useNavigate, useSearchParams, useLocation } from "react-router-dom"
import {
  Pencil,
  ExternalLink,
  AlertCircle,
  Info,
  Calendar,
  Settings,
  Users,
  PoundSterling,
  Briefcase
} from "lucide-react"
import axios from "axios"
import RecommendationModal from "../../../components/RecommendationModal"
import MissingRequirementsModal from "../../../components/MissingRequirementsModal"

/**
 * Publish requirements:
 * 1. Basic Info fields (all required):
 *    - Role title
 *    - Working style
 *    - Compensation
 *    - Hiring lead name
 *    - Hiring lead email
 *    - Hiring lead LinkedIn
 * 2. Either a Role Spotlight (video) OR a Job Description
 */

const ReviewRole = () => {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const location = useLocation()
  const queryRoleId = searchParams.get("roleId") || localStorage.getItem("currentRoleId")
  const [showPublishModal, setShowPublishModal] = useState(false)
  const [publishSuccessScenario, setPublishSuccessScenario] = useState(null)

  // Add states for recommendation modal
  const [showRecommendationModal, setShowRecommendationModal] = useState(false)
  const [isGeneratingRecommendations, setIsGeneratingRecommendations] = useState(false)
  const [progressStatus, setProgressStatus] = useState("")

  const [token, setToken] = useState("")
  const [roleId, setRoleId] = useState(null)
  const [roleFields, setRoleFields] = useState({})
  const [loading, setLoading] = useState(true)
  const [publishError, setPublishError] = useState(null)

  const [activeTab, setActiveTab] = useState("company")
  const [isNewStartup, setIsNewStartup] = useState(true)
  const [hasBookedCall] = useState(() => localStorage.getItem("hasBookedCall") === "true")

  // --- Declare state for manual tags from navigation at top level ---
  const [manualMustHaveTags, setManualMustHaveTags] = useState(null);
  const [manualNiceToHaveTags, setManualNiceToHaveTags] = useState(null);
  // --- End state declaration ---

  // Heading changes depending on whether the role is fully ready or not
  const [headingText, setHeadingText] = useState("It's time to publish!")
  const [isPublishDisabled, setIsPublishDisabled] = useState(false)

  // "What's next?" is only visible if no fields are missing
  const [showWhatsNext, setShowWhatsNext] = useState(false)
  // For the dynamic "What's next?" text based on day/time
  const [whatsNextText, setWhatsNextText] = useState("")

  // For truncating Key Skills / Interview Process
  const [showFullKeySkills, setShowFullKeySkills] = useState(false)
  const [showFullInterview, setShowFullInterview] = useState(false)

  // For card auto-scaling
  const cardRef = useRef(null)
  const [scale, setScale] = useState(1)

  // Add state for the new modal
  const [showMissingRequirementsModal, setShowMissingRequirementsModal] = useState(false);

  // Check if we're in the late week period (Friday after 4pm through Monday)
  const [isNextWeekClosure, setIsNextWeekClosure] = useState(false)
  const [nextMondayDate, setNextMondayDate] = useState("")

  // On load, fetch data and check for passed state
  useEffect(() => {
    const storedToken = localStorage.getItem("token")
    setToken(storedToken || "")
    setRoleId(queryRoleId || null)

    if (!queryRoleId) {
      localStorage.removeItem("hasBookedCall")
    }

    // --- Check location state (using hook's location object) ---
    if (location.state?.manualMustHaveTags !== undefined) {
      setManualMustHaveTags(location.state.manualMustHaveTags);
      console.log("[Review Page] Received Manual Must Have Tags from Nav State:", location.state.manualMustHaveTags);
    }
    if (location.state?.manualNiceToHaveTags !== undefined) {
      setManualNiceToHaveTags(location.state.manualNiceToHaveTags);
      console.log("[Review Page] Received Manual Nice To Have Tags from Nav State:", location.state.manualNiceToHaveTags);
    }
    // --- End Check location state ---

    const companySpotlight = JSON.parse(localStorage.getItem("selectedCompanySpotlight") || "null")
    const roleSpotlight = JSON.parse(localStorage.getItem("selectedRoleSpotlight") || "null")

    if (storedToken && queryRoleId) {
      localStorage.setItem("currentRoleId", queryRoleId)
      fetchRole(queryRoleId, storedToken, companySpotlight, roleSpotlight)

      const hasExistingSpotlights = companySpotlight || roleSpotlight
      setIsNewStartup(!hasExistingSpotlights)
    } else {
      setLoading(false)
    }
  }, [queryRoleId, location])

  // Check if we're in the late week period (Friday after 4pm through Monday)
  useEffect(() => {
    // Determine if we're in the Friday 4pm - Monday midnight window
    const now = new Date()
    const day = now.getDay() // 0 = Sunday, 1 = Monday, ..., 6 = Saturday
    const hour = now.getHours()
    
    // Check if we're between Friday 4pm and Monday midnight (extended for bank holiday)
    const isLateWeek = (day === 5 && hour >= 16) || day === 6 || day === 0 || day === 1
    
    if (isLateWeek) {
      // Calculate the next Monday (not the Monday after next)
      const nextMonday = new Date()
      // Find the immediately upcoming Monday (or add 7 days if today is Monday)
      const daysUntilNextMonday = day === 1 ? 7 : (8 - day) % 7 || 7
      nextMonday.setDate(nextMonday.getDate() + daysUntilNextMonday)
      
      const formattedDate = nextMonday.toLocaleDateString('en-US', { month: 'long', day: 'numeric' })
      
      // Store formatted date and set flag for next week closure
      setNextMondayDate(formattedDate)
      setIsNextWeekClosure(true)
      setWhatsNextText(`Once published, your role will be live and open to candidates until midnight next Monday (${formattedDate}). We'll be in touch as applications come in`)
    } else {
      setIsNextWeekClosure(false)
      setWhatsNextText("Once published, your role will be live and open to candidates until midnight this Monday. We'll be in touch as applications come in")
    }
  }, [])

  // After roleFields changes or after we finish loading, try to scale
  useEffect(() => {
    if (!loading) {
      scaleCardToFit()
    }
    // eslint-disable-next-line
  }, [roleFields, loading])

  const fetchRole = async (id, authToken, companySpotlight, roleSpotlight) => {
    try {
      const response = await axios.get("/api/roles/fetchRoleById", {
        headers: { Authorization: `Bearer ${authToken}` },
        params: { id },
      })

      if (response.data.role) {
        const role = response.data.role
        const fields = role.fields || {}

        // Transform data for easier checks
        const transformedData = {
          id: role.id || "",
          // Basic info
          role_title: fields["Role Title"] || "",
          working_style: fields["Working Style"] || "",
          compensation: fields["Compensation"] || "",
          hiring_lead_name: fields["Hiring Lead Name"] || "",
          hiring_lead_email: fields["Hiring Lead Email Address"] || "",
          hiring_lead_phone: fields["Hiring Lead Phone Number"] || "",
          hiring_lead_linkedin: fields["Hiring Lead Linkedin"] || "",
          // Company data
          company_name: fields["Name (from Startup)"]?.[0] || "",
          company_description: fields["Startup Description (from Startup)"] || "",
          company_website: fields["Website (from Startup)"] || "",
          team_size: fields["Team Size (from Startup)"] || "",
          location: fields["Location (from Startup)"] || "",
          // Extended job details
          key_skills: fields["Specific Experience"] || "",
          interview_process: fields["Interview Process"] || "",
          interview_link: fields["Hiring Lead Calendly Link"] || "",
          job_description_url: fields["Job Description PDF"] || "",
          spotlight_context: fields["Spotlight Context"] || "",
          // Spotlights
          company_dropbox_url: companySpotlight?.["URL Dropbox cleaned"] || null,
          company_loom_url: companySpotlight?.["URL Loom cleaned"] || null,
          role_dropbox_url: roleSpotlight?.["URL Dropbox cleaned"] || null,
          role_loom_url: roleSpotlight?.["URL Loom cleaned"] || null,
          // Hiring priorities - Fetch from new Airtable fields
          must_have_tags: fields["Must Haves"] || "", // Use new field name
          nice_to_have_tags: fields["Nice Haves"] || "", // Use new field name
        }
        setRoleFields(transformedData)
      }
    } catch (err) {
      console.error("Error fetching role data:", err)
      setPublishError("Failed to load role data.")
    } finally {
      setLoading(false)
    }
  }

  // Basic "ready to publish" checks
  const hasRoleSpotlight = !!(roleFields.role_loom_url || roleFields.role_dropbox_url)
  const hasJobDescription = !!roleFields.job_description_url

  const requiredMissing = () => {
    const missing = []
    // Basic Info
    if (!roleFields.role_title) missing.push("Role Title")
    if (!roleFields.working_style) missing.push("Working Style")
    if (!roleFields.compensation) missing.push("Compensation")
    if (!roleFields.hiring_lead_name) missing.push("Hiring Lead Name")
    if (!roleFields.hiring_lead_email) missing.push("Hiring Lead Email")
    // Phone number is no longer required
    if (!roleFields.hiring_lead_linkedin) missing.push("Hiring Lead LinkedIn")

    // Must have EITHER Role spotlight or JD
    if (!hasRoleSpotlight && !hasJobDescription) {
      missing.push("Role Spotlight or Job Description")
    }
    return missing
  }

  // Switch heading and disable Publish if anything's missing
  useEffect(() => {
    if (!loading) {
      const missing = requiredMissing()
      const anythingMissing = missing.length > 0

      setIsPublishDisabled(anythingMissing)
      setHeadingText(anythingMissing ? "Almost there!" : "It's time to publish!")
      setShowWhatsNext(!anythingMissing) // If nothing missing, show "What's next?"
    }
    // eslint-disable-next-line
  }, [roleFields, loading])

  // --- Internal function to handle the actual publishing API call and next steps ---
  const _publishRoleAndProceed = async () => {
    setPublishError(null); // Clear previous errors
    if (!roleId || !token) return; // Should not happen if called correctly, but safety check

    const chosenIDs = getSpotlightIds()
    try {
      await axios.patch(
        "/api/roles/updateRoleData",
        {
          fields: {
            "Open to applications": "Open to apps",
            Spotlight: chosenIDs,
          },
          roleId,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        },
      )
      console.log("Role published successfully via _publishRoleAndProceed");
      
      // Successfully published, now show the recommendation modal
      setShowPublishModal(false); // Ensure confirmation modal is closed if open
      setShowRecommendationModal(true);

    } catch (error) {
      console.error("Error publishing role:", error)
      setShowPublishModal(false); // Close confirmation modal on error too
      setPublishError("Failed to publish role. Please try again.");
    }
  };
  // --- End internal function ---

  const handlePublish = async (/* confirmedPublish = false */) => { // confirmedPublish no longer needed
    setPublishError(null); // Clear previous errors
    if (!roleId || !token) return

    const missing = requiredMissing()
    if (missing.length > 0) {
      setPublishError("Some required fields are still missing.")
      return
    }

    // Always show confirmation modal first
    setShowPublishModal(true);
    // _publishRoleAndProceed will be called when user confirms in the modal
  }

  // Handle direct publishing after skipping recommendations
  const handleSkipRecommendations = () => {
    setShowRecommendationModal(false)
    // No need to call handleDirectPublish anymore if its only purpose was publishing
    // If handleDirectPublish did other things (like navigation), keep it but remove publish logic.
    // Assuming it only published, we can just close the modal.
    // If navigation or other success UI is needed after skipping, add it here.
    console.log("Recommendations skipped. Role already published.");
    // Maybe navigate or show a success message that doesn't rely on PublishSuccessModal
    // For now, just close modal. Add navigation if needed: navigate('/roles'); 
  }
  
  // Extracted logic for generating recommendations using JD analysis
  const startRecsWithJDAnalysis = async () => {
    // Role is already published by handlePublish, just start the analysis/recs
    setIsGeneratingRecommendations(true);
    // No need to setShowRecommendationModal(true) here unless it was closed and needs reopening
    // Let's assume it's already open or handleGenerateRecommendations manages it.
    setProgressStatus("Analyzing job requirements..."); 

    try {
      // Step 0: Publish the role (MOVED TO handlePublish)
      // const chosenIDs = getSpotlightIds()
      // await axios.patch(...) // REMOVED

      // Step 1: Analyze JD via API
      const jdAnalysisResponse = await axios.post(
        "/api/candidates/analyzeJobDescription",
        { roleId },
        { headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" } }
      );
      
      let customJDAnalysis = {};
      if (jdAnalysisResponse.data.jdAnalysis) {
        customJDAnalysis = jdAnalysisResponse.data.jdAnalysis;
        console.log("Received JD analysis from API:", customJDAnalysis);
      } else {
        console.warn("JD Analysis API did not return results. Using empty analysis.");
        customJDAnalysis = { must_have: "", nice_to_have: "", experience: "", industryFocus: "" };
      }

      // --- Proceed with Phase 1 and Phase 2 using the fetched analysis --- 
      await runRecommendationPhases(customJDAnalysis, false); // Pass analysis and useUserTags=false

    } catch (error) {
      // Handle errors specifically for this path
      console.error("Error generating recommendations via JD analysis:", error);
      setProgressStatus(`Error: ${error.message || "Failed to generate recommendations via JD"}`);
      await new Promise(resolve => setTimeout(resolve, 3000));
      // Still need to close modal and show publish success as role was published
      setIsGeneratingRecommendations(false);
      setShowRecommendationModal(false);
      // No longer set publish success scenario since congrats is in RecommendationModal
      // let scenario = "nothingMissing"; // Assume base success for publish
      // if (!hasJobDescription && hasRoleSpotlight) scenario = "jdMissing";
      // if (hasJobDescription && !hasRoleSpotlight) scenario = "spotlightMissing";
      // setPublishSuccessScenario(scenario);
    }
  };

  // Extracted logic for generating recommendations using manual tags
  const startRecsWithManualTags = async (mustTags, niceTags) => {
    // Role is already published by handlePublish, just start the analysis/recs
    setIsGeneratingRecommendations(true);
    // No need to setShowRecommendationModal(true) here unless it was closed and needs reopening
    setProgressStatus("Using manual requirements...");

    try {
      // Step 0: Publish the role (MOVED TO handlePublish)
      // const chosenIDs = getSpotlightIds()
      // await axios.patch(...) // REMOVED

      // Step 1: Construct analysis directly from manual tags
      const customJDAnalysis = {
        must_have: mustTags || "",
        nice_to_have: niceTags || "",
        experience: "", 
        industryFocus: ""
      };
      console.log("Constructed JD analysis directly from manual tags:", customJDAnalysis);

      // --- Proceed with Phase 1 and Phase 2 using the manual analysis --- 
      await runRecommendationPhases(customJDAnalysis, true); // Pass analysis and useUserTags=true

    } catch (error) {
       // Handle errors specifically for this path
      console.error("Error generating recommendations via manual tags:", error);
      setProgressStatus(`Error: ${error.message || "Failed to generate recommendations via manual tags"}`);
      await new Promise(resolve => setTimeout(resolve, 3000));
      // Still need to close modal and show publish success as role was published
      setIsGeneratingRecommendations(false);
      setShowRecommendationModal(false);
      // No longer set publish success scenario since congrats is in RecommendationModal
      // let scenario = "nothingMissing"; // Assume base success for publish
      // if (!hasJobDescription && hasRoleSpotlight) scenario = "jdMissing";
      // if (hasJobDescription && !hasRoleSpotlight) scenario = "spotlightMissing";
      // setPublishSuccessScenario(scenario);
    }
  };

  // Extracted common logic for Phase 1, Phase 2, and Saving
  const runRecommendationPhases = async (customJDAnalysis, useUserTags) => {
      // This function now contains the logic previously starting from "Step 2: Start Phase 1"
      // It takes the prepared customJDAnalysis and useUserTags flag as arguments.

      console.log("JD Analysis complete, starting phase 1...")

      // Step 2: Start Phase 1 of the recommendations
      setProgressStatus("Finding matching candidates (Phase 1)...");
      const phase1Response = await axios.post(
        "/api/candidates/getRecommendations",
        { roleId, phase: 1, customJDAnalysis, useCustomTags: useUserTags },
        { headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" }, timeout: 120000 }
      );

      console.log("Phase 1 complete, checking results...")

      const { topCandidatesByEmbedding } = phase1Response.data
      if (!topCandidatesByEmbedding || topCandidatesByEmbedding.length === 0) {
        if (useUserTags) {
          throw new Error("No matching candidates found using manual tags. Try refining your \'Must Haves\'/\'Nice Haves\' requirements.");
        } else {
          throw new Error("No matching candidates found in phase 1");
        }
      }

      console.log(`Found ${topCandidatesByEmbedding.length} candidates in phase 1, starting phase 2...`)

      // Step 3: Start Phase 2 (GPT ranking) and wait for completion
      setProgressStatus(`Evaluating ${topCandidatesByEmbedding.length} candidates...`)
      const phase2Response = await axios.post(
        "/api/candidates/getRecommendations",
        { roleId, phase: 2, candidateEmbeddings: topCandidatesByEmbedding, customJDAnalysis },
        { headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" }, timeout: 300000 }
      );

      console.log("Phase 2 complete, preparing to save results...")

      const recommendedCandidates = phase2Response.data.recommendedCandidates || []
      const allRankedCandidates = phase2Response.data.allRankedCandidates || []

      if (recommendedCandidates.length === 0 && (!allRankedCandidates || allRankedCandidates.length === 0)) {
         throw new Error("No recommendations generated in Phase 2");
      }

      // Build recommendation map
      const recommendationMap = {}
      if (allRankedCandidates && allRankedCandidates.length > 0) {
        allRankedCandidates.forEach(candidate => {
          recommendationMap[candidate.id] = {
            score: candidate.score,
            reason: candidate.reason,
            isTopGPTCandidate: recommendedCandidates.some(rc => rc.id === candidate.id)
          }
        })
      } else {
        // Fallback if allRankedCandidates is empty but topCandidatesByEmbedding is not
        topCandidatesByEmbedding.forEach(item => {
          const candidateId = item?.candidate?.id
          if (candidateId) {
            recommendationMap[candidateId] = {
              score: Math.round(item.similarity * 100),
              reason: "Matched based on CV content and required skills (fallback)."
            }
          }
        })
        // Still attempt to mark top candidates based on recommendedCandidates if available
        recommendedCandidates.forEach(rec => {
          if (rec.id in recommendationMap) {
            recommendationMap[rec.id].score = rec.score; // Update score/reason if available
            recommendationMap[rec.id].reason = rec.reason;
            recommendationMap[rec.id].isTopGPTCandidate = true;
          }
        });
      }

      const candidateIds = Object.keys(recommendationMap)

      if (candidateIds.length === 0) {
        throw new Error("No candidates found to save")
      }

      // Step 4: Save to Airtable
      setProgressStatus(`Saving ${candidateIds.length} recommended candidates...`)
      const saveData = {
        roleId,
        candidates: candidateIds,
        recommendationData: recommendationMap,
        jdAnalysis: {
          TagsMustHave: Array.isArray(customJDAnalysis.must_have) 
            ? customJDAnalysis.must_have.join(", ") 
            : (customJDAnalysis.must_have || ""),
          TagsNiceToHave: Array.isArray(customJDAnalysis.nice_to_have) 
            ? customJDAnalysis.nice_to_have.join(", ") 
            : (customJDAnalysis.nice_to_have || ""),
          TagsExperience: (customJDAnalysis.experience && typeof customJDAnalysis.experience === 'object' && customJDAnalysis.experience.domains)
            ? customJDAnalysis.experience.domains.join(", ")
            : (customJDAnalysis.experience || ""),
          TagsIndustryFocus: customJDAnalysis.industryFocus || "",
          FullJDAnalysis: JSON.stringify(customJDAnalysis || {}),
          UsingUserSelectedTags: useUserTags
        }
      }

      await axios.post(
        "/api/candidates/saveRecommendationsToNewTables",
        saveData,
        { headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" }, timeout: 60000 }
      );

      console.log(`Successfully saved ${candidateIds.length} recommendations to Airtable`)
      setProgressStatus(`✓ Found ${candidateIds.length} matching candidates!`)
      await new Promise(resolve => setTimeout(resolve, 3000)) // Allow user to see success msg

      // Success completion - close modal
      setIsGeneratingRecommendations(false);
      setShowRecommendationModal(false);
  };

  // Initial handler when user clicks "Yes" on the first recommendation modal
  const handleGenerateRecommendations = async () => {
    // Role should already be published by handlePublish if this point is reached.
    setShowRecommendationModal(true); // Ensure modal is open for progress display
    setIsGeneratingRecommendations(true); // Set loading state

    // Check for manual tags (Nav state first, then Airtable)
    let tagsFromNav = !!(manualMustHaveTags || manualNiceToHaveTags);
    let tagsFromAirtable = !!(roleFields.must_have_tags || roleFields.nice_to_have_tags);
    let useUserTags = tagsFromNav || tagsFromAirtable;

    if (useUserTags) {
      // Tags exist - proceed directly with manual tag logic
      console.log("Manual tags found. Proceeding with manual tag recommendation path.");
      const mustTags = tagsFromNav ? manualMustHaveTags : roleFields.must_have_tags;
      const niceTags = tagsFromNav ? manualNiceToHaveTags : roleFields.nice_to_have_tags;
      // Don't set loading/progress here, let startRecsWithManualTags handle it
      startRecsWithManualTags(mustTags, niceTags); 
    } else {
      // No tags found - show the MissingRequirementsModal
      console.log("No manual tags found. Showing Missing Requirements modal.");
      setShowRecommendationModal(false); // Close the current modal
      setShowMissingRequirementsModal(true); // Show the new modal
      // Don't set loading/progress here, wait for user action
    }
  };

  // Handler for the "Go Back" button in MissingRequirementsModal
  const handleGoBackToJD = () => {
    setShowMissingRequirementsModal(false);
    navigate(`/create-role/job-description?roleId=${roleId}`);
  };

  // Handler for the "Use JD" button in MissingRequirementsModal
  const handleUseJDForRecs = () => {
    setShowMissingRequirementsModal(false);
    // Now start the process that uses JD analysis
    startRecsWithJDAnalysis();
  };

  // Helper function to get chosenIDs for spotlights
  const getSpotlightIds = () => {
    const chosenIDs = []
    const sc = localStorage.getItem("selectedCompanySpotlight")
    const sr = localStorage.getItem("selectedRoleSpotlight")
    if (sc) {
      const parsed = JSON.parse(sc)
      if (parsed?.id) chosenIDs.push(parsed.id)
    }
    if (sr) {
      const parsed = JSON.parse(sr)
      if (parsed?.id) chosenIDs.push(parsed.id)
    }
    return chosenIDs
  }

  const handleSaveDraft = async () => {
    if (!roleId || !token) return

    const chosenIDs = []
    const sc = localStorage.getItem("selectedCompanySpotlight")
    const sr = localStorage.getItem("selectedRoleSpotlight")
    if (sc) {
      const parsed = JSON.parse(sc)
      if (parsed?.id) chosenIDs.push(parsed.id)
    }
    if (sr) {
      const parsed = JSON.parse(sr)
      if (parsed?.id) chosenIDs.push(parsed.id)
    }

    try {
      await axios.patch(
        "/api/roles/updateRoleData",
        {
          fields: {
            "First saved": true,
            Spotlight: chosenIDs,
          },
          roleId,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        },
      )
      navigate("/roles")
    } catch (err) {
      console.error("Error saving draft:", err)
      setPublishError("Failed to save draft.")
    }
  }

  // If there's no "company" or "role" video, show a red "i" next to the tab
  const companyHasVideo = !!(roleFields.company_loom_url || roleFields.company_dropbox_url)
  const roleHasVideo = !!(roleFields.role_loom_url || roleFields.role_dropbox_url)

  const renderTabTitle = (tabName) => {
    let label = tabName === "company" ? "Company" : "Role"
    let hasVideo = tabName === "company" ? companyHasVideo : roleHasVideo

    return (
      <>
        {label}
        {!hasVideo && (
          <Info
            className="w-4 h-4 ml-1 text-red-500"
            title="No spotlight video attached"
          />
        )}
      </>
    )
  }

  // Show the actual video or fallback
  const renderSpotlightVideo = (type) => {
    const loomUrl = type === "company" ? roleFields.company_loom_url : roleFields.role_loom_url
    const dropboxUrl = type === "company" ? roleFields.company_dropbox_url : roleFields.role_dropbox_url

    if (loomUrl) {
      const loomId = loomUrl.split("/").pop()
      return (
        <iframe
          src={`https://www.loom.com/embed/${loomId}`}
          className="w-full h-[150px] object-cover rounded-lg bg-gray-100"
          frameBorder="0"
          allowFullScreen
        />
      )
    } else if (dropboxUrl) {
      const directVideoUrl = dropboxUrl.replace("?dl=0", "?raw=1")
      return (
        <video
          src={directVideoUrl}
          className="w-full h-[150px] object-cover rounded-lg bg-gray-100"
          controls
        />
      )
    }

    // Show special message if they've booked a call
    if (hasBookedCall) {
      return (
        <div className="w-full h-[150px] flex flex-col items-center justify-center bg-[#08f2c3]/5 rounded-lg border-2 border-dashed border-[#08f2c3]">
          <Calendar className="w-6 h-6 text-[#08f2c3] mb-2" />
          <p className="text-sm text-gray-600 text-center px-4">
            Call booked! We'll help you record your spotlights during the session.
          </p>
        </div>
      )
    }

    return (
      <div className="w-full h-[150px] flex items-center justify-center bg-gray-100 rounded-lg">
        <p className="text-xs text-gray-500">
          No {type} spotlight video available
        </p>
      </div>
    )
  }

  // Build a list of clickable "missing fields"
  // Each item navigates to the correct page for editing
  const getMissingFields = () => {
    const missing = []
    const fields = requiredMissing()

    fields.forEach((item) => {
      switch (item) {
        case "Role Title":
        case "Working Style":
        case "Compensation":
        case "Hiring Lead Name":
        case "Hiring Lead Email":
        case "Hiring Lead LinkedIn":
          missing.push({
            label: item,
            onClick: () => navigate(`/create-role/basic-info?roleId=${roleId}`),
          })
          break
        case "Role Spotlight or Job Description":
          // They can add either job description or spotlight
          missing.push({
            label: "Add a Job Description",
            onClick: () => navigate(`/create-role/job-description?roleId=${roleId}`),
          })
          missing.push({
            label: "Add a Role Spotlight",
            onClick: () => {
              if (isNewStartup) {
                navigate(`/create-role/spotlight?roleId=${roleId}`)
              } else {
                navigate(`/create-role/select-spotlight?roleId=${roleId}`)
              }
            },
          })
          break
        default:
          break
      }
    })
    return missing
  }

  // Helper to check if everything is loaded and then scale the card
  const scaleCardToFit = () => {
    if (!cardRef.current) return

    // Reduce the margin to ensure headings and modals fit better
    const paddingFromHeaderFooter = 120

    const cardHeight = cardRef.current.offsetHeight
    const windowHeight = window.innerHeight - paddingFromHeaderFooter

    // If the card is taller than the available viewport, shrink it
    if (cardHeight > windowHeight) {
      const newScale = windowHeight / cardHeight
      setScale(newScale < 0.6 ? 0.6 : newScale) // Don't shrink below 60%, increased from 50%
    } else {
      setScale(1)
    }
  }

  // Re-scale whenever the window resizes, or data changes
  useEffect(() => {
    window.addEventListener("resize", scaleCardToFit)
    return () => window.removeEventListener("resize", scaleCardToFit)
  }, [])

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <p>Loading role data...</p>
      </div>
    )
  }

  const missingFieldsList = getMissingFields()

  return (
    <div className="h-screen bg-white overflow-hidden flex flex-col">
      {/* Add recommendation modal with progress status */}
      <RecommendationModal 
        isOpen={showRecommendationModal}
        onClose={handleSkipRecommendations}
        onConfirm={handleGenerateRecommendations}
        isLoading={isGeneratingRecommendations}
        progressStatus={progressStatus}
        roleId={roleId}
      />
      
      {/* Heading */}
      <div className="pt-10 pb-6 flex-shrink-0">
        <h1 className="text-4xl font-semibold text-center">{headingText}</h1>
      </div>

      <div className="flex-1 max-w-6xl mx-auto px-4 md:px-8 flex items-center">
        {/* Updated container to use flex layout for vertical centering */}
        <div className="flex flex-col md:flex-row items-start justify-center gap-16 w-full">
          {/* Preview Card (auto-scaling wrapper) */}
          <div
            className="w-full md:w-[448px] mb-8"
            style={{
              transform: `scale(${scale})`,
              transformOrigin: "top center",
              transition: "transform 0.25s ease",
            }}
          >
            <div ref={cardRef} className="bg-white rounded-xl shadow-lg overflow-hidden border border-gray-200">
              <div className="p-4">
                {/* Spotlight Section */}
                <div className="mb-4">
                  <div className="flex justify-between items-center mb-2">
                    <div className="flex gap-4">
                      <button
                        onClick={() => setActiveTab("company")}
                        className={`text-xs font-medium pb-1 ${
                          activeTab === "company"
                            ? "text-[#08f2c3] border-b-2 border-[#08f2c3]"
                            : "text-gray-500"
                        }`}
                      >
                        {renderTabTitle("company")}
                      </button>
                      <button
                        onClick={() => setActiveTab("role")}
                        className={`text-xs font-medium pb-1 ${
                          activeTab === "role"
                            ? "text-[#08f2c3] border-b-2 border-[#08f2c3]"
                            : "text-gray-500"
                        }`}
                      >
                        {renderTabTitle("role")}
                      </button>
                    </div>
                    <button
                      onClick={() => {
                        if (isNewStartup) {
                          navigate("/create-role/spotlight?roleId=" + roleId)
                        } else {
                          navigate("/create-role/select-spotlight?roleId=" + roleId)
                        }
                      }}
                      className="p-1 text-gray-600 hover:text-gray-900 rounded-full hover:bg-gray-100"
                    >
                      <Pencil className="w-4 h-4" />
                    </button>
                  </div>
                  {renderSpotlightVideo(activeTab)}

                  {roleFields.spotlight_context && (
                    <p className="mt-2 text-xs text-gray-600">
                      {roleFields.spotlight_context}
                    </p>
                  )}
                </div>

                {/* Role and Company Info */}
                <div className="mb-4">
                  <div className="flex justify-between items-start">
                    <div>
                      <h2 className="text-sm font-semibold">
                        {roleFields.role_title || "(No role title)"}
                      </h2>
                      <p className="text-xs text-gray-600">
                        {roleFields.company_name || "Unnamed Company"}
                      </p>
                    </div>
                    <button
                      onClick={() => navigate(`/create-role/basic-info?roleId=${roleId}`)}
                      className="p-1 text-gray-600 hover:text-gray-900 rounded-full hover:bg-gray-100"
                    >
                      <Pencil className="w-4 h-4" />
                    </button>
                  </div>
                </div>

                <div className="flex flex-wrap gap-2 mb-4">
                  {/* Compensation tag with PoundSterling icon */}
                  {roleFields.compensation ? (
                    <span className="inline-flex mr-2 mb-2 items-center gap-x-1.5 rounded-md bg-purple-100 px-4 py-1 text-sm md:text-md font-medium text-purple-700">
                      <PoundSterling className="w-4 h-4" />
                      Salary range: {roleFields.compensation}
                    </span>
                  ) : (
                    <span className="inline-flex mr-2 mb-2 items-center gap-x-1.5 rounded-md bg-orange-100 px-4 py-1 text-sm md:text-md font-medium text-orange-700">
                      <AlertCircle className="size-5" />
                      Compensation missing
                    </span>
                  )}
                  
                  {/* Working style tag with Briefcase icon */}
                  {roleFields.working_style ? (
                    <span className="inline-flex mr-2 mb-2 items-center gap-x-1.5 rounded-md bg-blue-100 px-4 py-1 text-sm md:text-md font-medium text-blue-700">
                      <Briefcase className="w-4 h-4" />
                      {roleFields.working_style}
                    </span>
                  ) : (
                    <span className="inline-flex mr-2 mb-2 items-center gap-x-1.5 rounded-md bg-orange-100 px-4 py-1 text-sm md:text-md font-medium text-orange-700">
                      <AlertCircle className="size-5" />
                      Working style missing
                    </span>
                  )}

                  {/* Team size tag */}
                  {roleFields.team_size && (
                    <span className="inline-flex mr-2 items-center gap-x-1 rounded-md bg-green-100 px-4 py-1 text-sm md:text-md font-medium text-green-700">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                        strokeWidth="1.5"
                        stroke="currentColor"
                        className="size-5"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M18 18.72a9.094 9.094 0 0 0 3.741-.479 3 3 0 0 0-4.682-2.72m.94 3.198.001.031c0 .225-.012.447-.037.666A11.944 11.944 0 0 1 12 21c-2.17 0-4.207-.576-5.963-1.584A6.062 6.062 0 0 1 6 18.719m12 0a5.971 5.971 0 0 0-.941-3.197m0 0A5.995 5.995 0 0 0 12 12.75a5.995 5.995 0 0 0-5.058 2.772m0 0a3 3 0 0 0-4.681 2.72 8.986 8.986 0 0 0 3.74.477m.94-3.197a5.971 5.971 0 0 0-.94 3.197M15 6.75a3 3 0 1 1-6 0 3 3 0 0 1 6 0Zm6 3a2.25 2.25 0 1 1-4.5 0 2.25 2.25 0 0 1 4.5 0Zm-13.5 0a2.25 2.25 0 1 1-4.5 0 2.25 2.25 0 0 1 4.5 0Z"
                        />
                      </svg>
                      {roleFields.team_size} people in the team
                    </span>
                  )}

                  {/* Location tag */}
                  {roleFields.location && (
                    <span className="flex inline-flex mr-2 items-center gap-x-1 rounded-md bg-blue-100 px-4 py-1 text-sm md:text-md font-medium text-blue-500">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                        strokeWidth="2"
                        stroke="currentColor"
                        className="size-4"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M15 10.5a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z"
                        />
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M19.5 10.5c0 7.142-7.5 11.25-7.5 11.25S4.5 17.642 4.5 10.5a7.5 7.5 0 1 1 15 0Z"
                        />
                      </svg>
                      {roleFields.location}
                    </span>
                  )}
                </div>

                {/* Company Description */}
                <div className="mb-4">
                  <div className="flex justify-between items-start mb-1">
                    <h3 className="text-xs font-semibold">About the company</h3>
                  </div>
                  <p className="text-xs text-gray-600 line-clamp-2 mb-1">
                    {roleFields.company_description || "No company description provided."}
                  </p>
                  {roleFields.company_website && (
                    <a
                      href={roleFields.company_website}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center text-xs text-[#08f2c3] hover:underline"
                    >
                      Visit website
                      <ExternalLink className="w-3 h-3 ml-1" />
                    </a>
                  )}
                </div>

                {/* Job Details Section */}
                <div className="mb-4">
                  <div className="flex justify-between items-start mb-1">
                    <h3 className="text-xs font-semibold">Job details</h3>
                    <button
                      onClick={() => navigate(`/create-role/job-description?roleId=${roleId}`)}
                      className="p-1 text-gray-600 hover:text-gray-900 rounded-full hover:bg-gray-100"
                    >
                      <Pencil className="w-3 h-3" />
                    </button>
                  </div>
                  {/* Key Skills (with toggle) */}
                  {roleFields.key_skills && (
                    <div className="mb-2">
                      <h4 className="text-xs font-medium text-gray-700">Key skills</h4>
                      <p
                        className={`
                          text-xs text-gray-600 whitespace-pre-line 
                          ${showFullKeySkills ? '' : 'line-clamp-1'} 
                          overflow-hidden
                        `}
                      >
                        {roleFields.key_skills}
                      </p>
                      <button
                        onClick={() => setShowFullKeySkills(!showFullKeySkills)}
                        className="mt-1 text-xs text-[#08f2c3] hover:underline"
                      >
                        {showFullKeySkills ? 'Show less' : 'Show more'}
                      </button>
                    </div>
                  )}

                  {/* Interview Process (with toggle) */}
                  {roleFields.interview_process && (
                    <div className="mb-2">
                      <h4 className="text-xs font-medium text-gray-700">Interview process</h4>
                      <p
                        className={`
                          text-xs text-gray-600 whitespace-pre-line 
                          ${showFullInterview ? '' : 'line-clamp-1'} 
                          overflow-hidden
                        `}
                      >
                        {roleFields.interview_process}
                      </p>
                      <button
                        onClick={() => setShowFullInterview(!showFullInterview)}
                        className="mt-1 text-xs text-[#08f2c3] hover:underline"
                      >
                        {showFullInterview ? 'Show less' : 'Show more'}
                      </button>
                    </div>
                  )}

                  {/* Job Description Link */}
                  {roleFields.job_description_url ? (
                    <a
                      href={roleFields.job_description_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center text-xs text-[#08f2c3] hover:underline mt-1"
                    >
                      View full job description
                      <ExternalLink className="w-3 h-3 ml-1" />
                    </a>
                  ) : (
                    <div className="flex items-center mt-1">
                      <AlertCircle className="w-3 h-3 mr-1 text-yellow-500" />
                      <span className="text-xs text-yellow-700">Job description missing</span>
                    </div>
                  )}
                </div>

                {/* Error message if any */}
                {publishError && (
                  <p className="text-xs text-red-500 mb-2">{publishError}</p>
                )}

                {/* Action Buttons */}
                <div className="space-y-3 mt-4">
                  <button
                    onClick={handleSaveDraft}
                    className="w-full px-4 py-3 bg-gray-100 text-gray-900 rounded-full text-sm font-semibold hover:bg-gray-200 transition-colors"
                  >
                    Save draft
                  </button>
                  <button
                    onClick={() => handlePublish()}
                    disabled={isPublishDisabled}
                    className="w-full px-4 py-3 bg-[#08f2c3] text-gray-900 rounded-full text-sm font-semibold
                      hover:bg-[#06c19c] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Publish role
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* MISSING FIELDS BOX if something is blocking publish */}
          {isPublishDisabled && (
            <div className="flex-1 max-w-md">
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <h2 className="text-lg font-semibold text-yellow-900 mb-2 flex items-center">
                  <AlertCircle className="w-5 h-5 mr-1" />
                  Missing fields
                </h2>
                <p className="text-sm text-yellow-900 mb-3">
                  {missingFieldsList.length === 2 && missingFieldsList[0].label.includes("Job Description") ? 
                    "You need to fill one of the following before publishing:" :
                    "You need to fill in the following before publishing:"
                  }
                </p>
                <ul className="space-y-2">
                  {missingFieldsList.map((item, idx) => (
                    <li key={idx}>
                      <button
                        onClick={item.onClick}
                        className="inline-flex items-center gap-1 px-3 py-2 text-sm font-medium
                          text-yellow-900 bg-yellow-100 rounded-md hover:bg-yellow-200
                          transition-colors"
                      >
                        <AlertCircle className="w-4 h-4" />
                        {item.label}
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          )}

          {/* WHAT'S NEXT if fully ready to publish (no missing fields) */}
          {!isPublishDisabled && showWhatsNext && (
            <div className="flex-1 max-w-md mt-2 md:mt-6">
              <h2 className="text-2xl font-semibold mb-5">What's next?</h2>
              <div className="space-y-5">
                <div className="flex gap-4">
                  <div className="flex-shrink-0 w-10 h-10 rounded-full bg-[#08f2c3]/10 flex items-center justify-center">
                    <Calendar className="w-5 h-5 text-[#08f2c3]" />
                  </div>
                  <div>
                    <h3 className="text-base font-medium mb-1">Start receiving applications</h3>
                    {isNextWeekClosure ? (
                      <p className="text-gray-600">
                        Once published, your role will be live and open to candidates until midnight next Monday ({nextMondayDate}). We'll be in touch as applications come in
                      </p>
                    ) : (
                      <p className="text-gray-600">
                        Once published, your role will be live and open to candidates until midnight this Sunday <span className="text-orange-500 font-medium"></span>. We'll be in touch as applications come in
                      </p>
                    )}
                  </div>
                </div>

                <div className="flex gap-4">
                  <div className="flex-shrink-0 w-10 h-10 rounded-full bg-[#08f2c3]/10 flex items-center justify-center">
                    <Settings className="w-5 h-5 text-[#08f2c3]" />
                  </div>
                  <div>
                    <h3 className="text-base font-medium mb-1">Browse and connect</h3>
                    <p className="text-gray-600">
                      View candidate profiles and send match requests to standout candidates at any time
                    </p>
                  </div>
                </div>

                <div className="flex gap-4">
                  <div className="flex-shrink-0 w-10 h-10 rounded-full bg-[#08f2c3]/10 flex items-center justify-center">
                    <Users className="w-5 h-5 text-[#08f2c3]" />
                  </div>
                  <div>
                    <h3 className="text-base font-medium mb-1">Find the perfect match</h3>
                    <p className="text-gray-600">
                      Interview top candidates and move closer to making your next great hire
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}
          {/* End of "What's next?" */}
        </div>
      </div>

      {/* Publish Confirmation Modal (if no JD) */}
      <PublishConfirmModal 
        isOpen={showPublishModal}
        onClose={() => setShowPublishModal(false)}
        onConfirm={_publishRoleAndProceed}
        hasJobDescription={!!roleFields.job_description_url}
        roleId={roleId}
      />

      {/* Render the new Missing Requirements Modal */}
      <MissingRequirementsModal 
        isOpen={showMissingRequirementsModal}
        onClose={() => setShowMissingRequirementsModal(false)} // Allow closing via backdrop for now
        onGoBack={handleGoBackToJD}
        onUseJD={handleUseJDForRecs}
      />
    </div>
  )
}

/* PublishConfirmModal: Shown if user tries to publish but is missing JD (yet has a spotlight).
   It warns "Publish without job description?" */
const PublishConfirmModal = ({ isOpen, onClose, onConfirm, hasJobDescription, roleId }) => {
  const navigate = useNavigate()
  const [isNextWeekModal, setIsNextWeekModal] = useState(false)
  const [nextMondayDateFormatted, setNextMondayDateFormatted] = useState("")
  
  useEffect(() => {
    // Determine if we're in the Friday 4pm - Monday midnight window
    const now = new Date()
    const day = now.getDay() // 0 = Sunday, 1 = Monday, ..., 6 = Saturday
    const hour = now.getHours()
    
    // Check if we're between Friday 4pm and Monday midnight (extended for bank holiday)
    const isLateWeek = (day === 5 && hour >= 16) || day === 6 || day === 0 || day === 1
    
    if (isLateWeek) {
      // Calculate the next Monday (not the Monday after next)
      const nextMonday = new Date()
      // Find the immediately upcoming Monday (or add 7 days if today is Monday)
      const daysUntilNextMonday = day === 1 ? 7 : (8 - day) % 7 || 7
      nextMonday.setDate(nextMonday.getDate() + daysUntilNextMonday)
      
      const formattedDate = nextMonday.toLocaleDateString('en-US', { month: 'long', day: 'numeric' })
      
      setNextMondayDateFormatted(formattedDate)
      setIsNextWeekModal(true)
    } else {
      setIsNextWeekModal(false)
    }
  }, [isOpen])

  if (!isOpen) return null

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose()
    }
  }

  const handleSaveForLater = () => {
    onClose();
    // Navigate back to roles page, same as "Save draft" functionality
    navigate("/roles");
  }

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
      onClick={handleBackdropClick}
    >
      <div className="bg-white rounded-lg p-6 max-w-md w-full">
        <h3 className="text-lg font-semibold mb-2">Publish role</h3>
        {isNextWeekModal ? (
          <p className="text-sm text-gray-600 mb-6">
            Are you sure? Once published, your role will be open to applicants until midnight next Sunday ({nextMondayDateFormatted}) - <span className="text-orange-500 font-medium"></span> We'll keep you posted on candidate interest.
          </p>
        ) : (
          <p className="text-sm text-gray-600 mb-6">
            Are you sure? Once published, candidates can start applying straight away. The role will automatically close at midnight on Sunday <span className="text-orange-500 font-medium"></span>.
          </p>
        )}
        <div className="flex justify-center gap-3">
          <button
            onClick={handleSaveForLater}
            className="px-4 py-2 text-sm font-medium text-[#08f2c3] hover:text-[#06c19c] border border-[#08f2c3] rounded-full"
          >
            Save for later
          </button>
          <button
            onClick={onConfirm}
            className="px-4 py-2 text-sm font-medium bg-[#08f2c3] text-black rounded-full hover:bg-[#06c19c]"
          >
            Yes, I'm sure
          </button>
        </div>
      </div>
    </div>
  )
}

export default ReviewRole
